/*  MUI  */
import {
    Box,
    Grid,
    LinearProgress,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core';
import {
    LifecyclePicker,
    ManagedPicker
} from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Usage } from 'application/Shared/Components/Usage';
import { Helpers, Route } from 'application/Shared/Helpers';

/*  Yarn  */
import i18n from 'i18n';

/*  Objects  */
import { Application } from 'objects/Application';
import {
    Breach,
    BreachDescription,
    BreachResources,
    BreachScore,
    BreachSteps
} from 'objects/Breach';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { withRouter } from 'router/withRouter';

/*  Components  */
import CoverPage from '../Components/Containers/CoverPage';

const headers = [
    {
        align: 'left',
        field: 'dateAnnounced',
        renderHeader: <strong>{i18n.t('Common.date')}</strong>,
        width: '10%'
    },
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{i18n.t('Common.application')}</strong>,
        width: '15%'
    },
    {
        align: 'left',
        field: 'description',
        renderHeader: <strong>{i18n.t('Common.description')}</strong>,
        width: '45%'
    },
    {
        align: 'left',
        field: 'severityScore',
        renderHeader: <strong>{i18n.t('Common.score')}</strong>,
        width: '10%'
    },
    {
        align: 'left',
        field: 'clientsImpacted',
        renderHeader: <strong>{i18n.t('Common.clients_impacted')}</strong>,
        width: '10%'
    },
    {
        align: 'left',
        field: 'usersImpacted',
        renderHeader: <strong>{i18n.t('Common.usersImpacted')}</strong>,
        width: '10%'
    }
];

const headersClients = [
    {
        field: 'client',
        renderHeader: i18n.t('Common.client'),
        width: '15%'
    },
    {
        field: 'numUsersImpacted',
        renderHeader: i18n.t('Common.num_users_impacted'),
        width: '10%'
    },
    {
        colSpan: 2,
        field: 'credentialExposure',
        renderHeader: i18n.t('Common.credential_exposure'),
        width: '15%'
    },
    {
        field: 'applicationState',
        renderHeader: i18n.t('Common.applicationState'),
        width: '20%'
    },
    {
        field: 'lifecycleStage',
        renderHeader: i18n.t('Common.lifecycleStage'),
        width: '15%'
    },
    {
        field: 'usage',
        renderHeader: i18n.t('Common.usage'),
        width: '10%'
    }
];

const ReportWrapper = (props) => {
    const {
        t,
        match,
        reportData,
        reportSetup,
        renderingScheduledReport,
        session
    } = props;

    if (reportData === undefined) {
        return null;
    }

    return (
        <Box>
            {reportSetup.reportSelections
                ?.partnerBreachImpactClientImpactCover && (
                <CoverPage reportSetup={reportSetup} />
            )}
            {reportData.partnerBreachImpactClientImpactBreakdown && (
                <Box m="32px">
                    <Report
                        key="breakdown"
                        t={t}
                        match={match}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData}
                        session={session}
                    />
                </Box>
            )}
        </Box>
    );
};

class Report extends Component {
    constructor(props) {
        super(props);

        this.state = {
            breachCount: 0,
            breachPages: [],
            loaded: false
        };
    }

    componentDidMount() {
        const { reportData } = this.props;
        const { partnerBreachImpactClientImpactBreakdown } = reportData;
        const data = partnerBreachImpactClientImpactBreakdown.reportData;

        const breachPages = [];
        Object.values(data).forEach((breach) => {
            breachPages.push(breach);
        });

        const breachCount = breachPages.length;
        this.setState({
            breachCount,
            breachPages,
            loaded: true
        });
    }

    renderRowOverview = (row) => {
        const { match } = this.props;
        const { partnerId } = match.params;
        const basePartnerRoute = Route.buildBasePartnerRoute({
            partnerId
        });

        return (
            <Breach data={row}>
                <TableCell>
                    <DateFormat date={row.dateAnnounced} noTimeString />
                </TableCell>
                <Application
                    key={row.id}
                    data={row.application}
                    nameLinkTo={`${basePartnerRoute}/applicationSettings`}
                >
                    <TableCell>
                        <ApplicationNameLinkCell withCategory />
                    </TableCell>
                </Application>
                <TableCell>
                    <BreachDescription lines={10} />
                </TableCell>
                <TableCell>
                    <BreachScore />
                </TableCell>
                <TableCell>{row.totalTenants}</TableCell>
                <TableCell>{row.totalUsers}</TableCell>
            </Breach>
        );
    };

    renderOverview = () => {
        const { t } = this.props;
        const { breachCount, breachPages } = this.state;
        if (breachCount === 0) {
            return null;
        }

        return (
            <Box mt="16px">
                <Typography variant="h1">
                    {t('VendorIncident.overview')}
                </Typography>
                <hr />

                <Box mt="32px">
                    <ManagedTable
                        headers={headers}
                        renderBody={this.renderRowOverview}
                        rows={breachPages}
                        rowsApiPreload
                        rowsPerPage={-1}
                        sortDirection="DESC"
                        sortFieldType="date"
                        sortKey="dateAnnounced"
                    />
                </Box>
            </Box>
        );
    };

    renderClient = ({ breach, row }) => {
        const { match, t } = this.props;
        const { partnerId } = match.params;

        const { name, userCount, totalUsers } = row;
        const { application } = breach;

        const clientId = row.id;
        const applicationId = application.id;

        const baseAdminRoute = Route.buildBaseAdminRoute({
            clientId,
            partnerId
        });

        const exposure =
            totalUsers === 0 ? 0 : parseInt((userCount / totalUsers) * 100, 10);

        return (
            <>
                <TableCell>
                    <Link
                        className="bold link underline"
                        to={`${baseAdminRoute}/application/${applicationId}/users`}
                        state={{ from: t('VendorIncident.partner') }}
                    >
                        {name}
                    </Link>
                </TableCell>
                <TableCell>
                    <Link
                        className="bold link underline"
                        to={`${baseAdminRoute}/application/${applicationId}/users`}
                        state={{ from: t('VendorIncident.partner') }}
                    >
                        {userCount} / {totalUsers}
                    </Link>
                    <br />
                    <small>({exposure}%&nbsp;Exposure)</small>
                </TableCell>
                <TableCell>
                    <LinearProgress
                        value={parseInt(row.credentialExposurePercent, 10)}
                        variant="determinate"
                    />
                    {row.credentialExposure.work}
                    &nbsp;Work
                    <br />
                    {row.credentialExposure.personal}
                    &nbsp;Personal
                </TableCell>
                <TableCell>
                    <strong>{row.credentialExposure.total} Total</strong>
                </TableCell>
                <Application data={breach.application}>
                    <TableCell>
                        <ManagedPicker visualOnly value={row.managementStage} />
                    </TableCell>
                    <TableCell align="left">
                        <LifecyclePicker visualOnly value={row.status} />
                    </TableCell>
                    <TableCell align="center">
                        <Usage usageChart={row.usageChart} chartOnly />
                    </TableCell>
                </Application>
            </>
        );
    };

    renderClients = (breach) => {
        const { t } = this.props;
        const { tenants } = breach;

        let hasImpact = true;
        if (Helpers.isEmpty(tenants)) {
            hasImpact = false;
        }

        return (
            <Box mt="32px" data-trackid="vendorIncidentRenderClientsContainer">
                {hasImpact ? (
                    <>
                        <Typography variant="h4">
                            {t('Common.clients_impacted')}
                        </Typography>
                        <ManagedTable
                            headers={headersClients}
                            renderBody={(row) =>
                                this.renderClient({ breach, row })
                            }
                            rows={tenants}
                            rowsPerPage={-1}
                            sortDirection="DESC"
                            sortFieldType="number"
                            sortKey="credentialExposure.total"
                        />
                    </>
                ) : (
                    <Typography variant="h4">
                        {t('Common.no_clients_impacted')}
                    </Typography>
                )}
            </Box>
        );
    };

    renderDetails = (breach) => {
        const { t, match } = this.props;
        const { partnerId } = match.params;

        const basePartnerRoute = Route.buildBasePartnerRoute({
            partnerId
        });

        if (!Helpers.isset(breach, 'application', 'id')) {
            return null;
        }

        const { id, application, dateAnnounced } = breach;

        return (
            <Box
                sx={{ '@media print': { marginTop: '8px' }, marginTop: '64px' }}
            >
                <Breach data={breach}>
                    <Typography variant="h1">
                        {t('VendorIncident.details')} - {application.name}
                    </Typography>
                    <hr />
                    <Box ml="16px" mr="16px">
                        <Grid container>
                            <Grid item xs={4}>
                                <Box>
                                    <Typography style={{ fontWeight: 600 }}>
                                        Date Announced
                                    </Typography>
                                    <DateFormat
                                        date={dateAnnounced}
                                        noTimeString
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Application
                                    key={id}
                                    data={application}
                                    nameLinkTo={`${basePartnerRoute}/applicationSettings`}
                                >
                                    <ApplicationNameLinkCell
                                        withCategory
                                        categoryLines={2}
                                    />
                                </Application>
                            </Grid>
                            <Grid item xs={4}>
                                <Box>
                                    <Typography style={{ fontWeight: 600 }}>
                                        Severity
                                    </Typography>
                                </Box>
                                <Box>
                                    <BreachScore />
                                </Box>
                            </Grid>
                        </Grid>
                        <Box my="8px">
                            <Typography style={{ fontWeight: 600 }}>
                                Description
                            </Typography>
                            <BreachDescription lines={10} />
                        </Box>
                        <Grid container spacing={3}>
                            <Grid item xs={3}>
                                <BreachResources linkHeight="28px" />
                            </Grid>
                            <Grid item xs={9}>
                                <BreachSteps />
                            </Grid>
                        </Grid>
                    </Box>
                </Breach>
                <div style={{ paddingTop: '20px' }}>
                    <hr />
                </div>
                <Box width="100%">{this.renderClients(breach)}</Box>
            </Box>
        );
    };

    render() {
        const { loaded, breachPages } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <>
                <Box className="pageBreak">{this.renderOverview()}</Box>
                {breachPages.map((breach) => (
                    <Box
                        key={`vendorIncident_${breach.id}_${breach.applicationId}`}
                        className="pageBreak"
                    >
                        {this.renderDetails(breach)}
                    </Box>
                ))}
            </>
        );
    }
}

export default withRouter(withTranslation()(ReportWrapper));
